import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { ArrowBack } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import { com } from '@eidu/web'
import useEntityTypes from '../../io/useEntityTypes'
import usePaginatedPermissionGrants from '../../io/usePaginatedPermissionGrants'
import useHasGlobalPermissions from './useHasGlobalPermissions'
import PermissionError from '../entity/PermissionError'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import { useEntitiesByIds } from '../../io/useEntities'
import { EMPTY_ENTITY_TYPES } from '../../io/useEntityTypes'
import { usePermissionPoliciesByIds } from '../../io/usePermissionPolicies'
import EqualityHashMap from '../../util/EqualityHashMap'
import { findEntityTypeByFieldId, findFieldById } from '../../util/EntityTypeExtensions'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntitySelectorChips from '../../components/input/EntitySelectorChips'
import UserTypesWithoutColumnWarning from '../../components/permissions/visualizer/UserTypesWithoutColumnWarning'
import VisibleColumnsPicker from '../../components/permissions/visualizer/VisibleColumnsPicker'
import VisualizerTableRow from '../../components/permissions/visualizer/VisualizerTableRow'
import {
  buildColumnHeaders,
  buildRowHeaders,
  collectSelectorFieldIds,
} from '../../components/permissions/visualizer/utils'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermissionPolicyId = com.eidu.sharedlib.entity.permission.PermissionPolicyId

const AccessControlVisualizerPage = () => {
  const navigate = useNavigate()
  const { entityTypes: entityTypesMap } = useEntityTypes()
  const entityTypes = useMemo(() => [...(entityTypesMap?.values() ?? [])], [entityTypesMap])
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())

  const { grants, hasMore, loading: loadingGrants, loadMore } = usePaginatedPermissionGrants({ pageSize: 200 })
  useEffect(() => {
    if (hasMore && !loadingGrants) loadMore()
  }, [hasMore, loadingGrants, loadMore])
  const grantsError: unknown = undefined

  const { hasPermission, loading: permissionsLoading } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)

  const userSelectors = useMemo(() => grants.map((grant) => grant.permissionGrant.userSelector), [grants])

  const selectorFieldIds = useMemo(() => userSelectors.flatMap(collectSelectorFieldIds), [userSelectors])

  const referencableEntityTypeIds = useMemo(() => {
    const ids = new Map<string, EntityTypeId>()
    selectorFieldIds.forEach((fieldId) => {
      const entityType = findEntityTypeByFieldId(entityTypes, fieldId)
      if (!entityType) return
      const field = findFieldById(entityType, fieldId)
      if (!field || field.type !== FieldType.Reference) return
      Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? []).forEach((typeId) => {
        ids.set(typeId.toString(), typeId)
      })
    })
    return Array.from(ids.values())
  }, [entityTypes, selectorFieldIds])

  const referencedEntitiesByTypeId =
    useEntitiesOfType({
      typeIds: referencableEntityTypeIds,
      allTypes: entityTypesMap ?? EMPTY_ENTITY_TYPES,
      pageSize: 100,
    }) ?? new Map()

  const singleTargetIds = useMemo(
    () =>
      grants.flatMap((grant) =>
        [...grant.permissionGrant.permissions.asJsReadonlySetView()]
          .filter(
            (p): p is EntitySelectorPermission =>
              p instanceof EntitySelectorPermission && p.targets instanceof EntitySelector.Single
          )
          .map((p) => (p.targets as EntitySelector.Single).entityId)
      ),
    [grants]
  )
  const { entities: singleTargetEntities } = useEntitiesByIds(singleTargetIds)

  const entitiesByTypeId = useMemo(() => {
    const merged = new EqualityHashMap<EntityTypeId, EqualityHashMap<EntityId, EntityWithLabelAndRelated>>(
      [...referencedEntitiesByTypeId.entries()].map(([typeId, entities]) => [typeId, new EqualityHashMap(entities)])
    )
    for (const entity of singleTargetEntities?.values() ?? []) {
      if (!entity) continue
      const typeId = entity.entity.typeId
      const inner = merged.get(typeId) ?? new EqualityHashMap<EntityId, EntityWithLabelAndRelated>()
      inner.set(entity.entity.id, entity)
      merged.set(typeId, inner)
    }
    return merged
  }, [referencedEntitiesByTypeId, singleTargetEntities])

  const referencedPolicyIds = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyId>()
    for (const grant of grants) {
      for (const id of grant.permissionGrant.policyIds.asJsReadonlySetView()) map.set(id, id)
    }
    return [...map.values()]
  }, [grants])
  const referencedPoliciesQuery = usePermissionPoliciesByIds(referencedPolicyIds)
  const policiesById = useMemo(() => {
    const map = new Map<string, (typeof referencedPoliciesQuery.policies)[number]>()
    for (const policy of referencedPoliciesQuery.policies) map.set(policy.policyId.toString(), policy)
    return map
  }, [referencedPoliciesQuery.policies])

  const columnHeaders = useMemo(
    () => buildColumnHeaders(grants, entityTypes, entitiesByTypeId, policiesById),
    [grants, entityTypes, entitiesByTypeId, policiesById]
  )

  const columnByKey = useMemo(() => new Map(columnHeaders.map((c) => [c.id, c])), [columnHeaders])

  const visibleColumnHeaders = useMemo(
    () => columnHeaders.filter((c) => !hiddenColumns.has(c.id)),
    [columnHeaders, hiddenColumns]
  )

  const rowHeaders = useMemo(() => buildRowHeaders(entityTypes), [entityTypes])

  if (permissionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!hasManagePermissionGrantsPermission) {
    return <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
  }

  return (
    <Stack padding={3} spacing={2}>
      <Collapse in={!!grantsError}>
        <Alert variant="filled" severity="error" sx={{ marginBottom: 2 }}>
          {String(grantsError)}
        </Alert>
      </Collapse>
      <UserTypesWithoutColumnWarning
        entityTypes={entityTypes}
        columnHeaders={columnHeaders}
        onGrantPermission={(typeId) =>
          navigate('/access-control/new', {
            state: { initialUserTypeId: typeId },
          })
        }
      />
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Back to Access Control">
            <IconButton
              size="small"
              onClick={() => navigate('/access-control')}
              sx={{ color: 'inherit', p: 0.25 }}
              aria-label="Back to Access Control"
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Access Control Visualizer
          </Typography>
        </Stack>
        {columnHeaders.length > 0 && (
          <VisibleColumnsPicker
            columnHeaders={columnHeaders}
            visibleColumnHeaders={visibleColumnHeaders}
            onChange={(visibleIds) =>
              setHiddenColumns(new Set(columnHeaders.filter((c) => !visibleIds.has(c.id)).map((c) => c.id)))
            }
          />
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader sx={{ width: 'auto', '& td, & th': { py: 0.25, px: 1 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ width: 220, minWidth: 220, borderRight: 1, borderColor: 'divider' }} />
                {visibleColumnHeaders.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{ minWidth: 140, borderRight: 1, borderColor: 'divider', textAlign: 'left', px: 1 }}
                  >
                    <EntitySelectorChips
                      selector={column.selector}
                      entityTypes={entityTypesMap ?? EMPTY_ENTITY_TYPES}
                      entitiesByTypeId={entitiesByTypeId}
                      direction="column"
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowHeaders.map((row) => (
                <VisualizerTableRow
                  key={row.id}
                  row={row}
                  visibleColumnHeaders={visibleColumnHeaders}
                  totalColumnCount={columnHeaders.length}
                  columnByKey={columnByKey}
                  entityTypes={entityTypes}
                  entitiesByTypeId={entitiesByTypeId}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
      {loadingGrants && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Stack>
  )
}

export default AccessControlVisualizerPage
